import { useGetAttachmentsByDocId } from "@/api/attachment";
import { getCollectors } from "@/api/collector/collector";
import { useGetMultiplePrint } from "@/api/multipleprint/multipleprint";
import MultiplePrintSingleDrawer from "@/components/multipleprint/MultiplePrintSingleDrawer";
import {
  useGetMultipleSampleById,
  useUpdateMultipleSample,
} from "@/api/multiplesample";
import * as AF from "@/components/form-items/AbstractForm";
import { DateQualifierFields } from "@/components/form-items/DateQualifierFields";
import {
  FourDimensionalInput,
  TwoDimensionalInput,
} from "@/components/form-items/DimensionsInput";
import MediaLibrary, { ImageFormData } from "@/components/images/MediaLibrary";
import { ExemplaireHistoryTable } from "@/components/multiprint/exemplaires/ExemplaireHistoryTable";
import ExemplaireCertificatDrawer from "@/components/multiprint/exemplaires/ExemplaireCertificatDrawer";
import { ExemplaireMouvementTable } from "@/components/multiprint/exemplaires/ExemplaireMouvementTable";
import { AdministrativeDocumentList } from "@/pages/dashboard/forms/artworks/AdministrativeDocumentList";
import StyledDrawer from "@/components/StyledDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SeparatorWithText } from "@/components/ui/separator";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ConfigurationTypeFile } from "@/config/forms/general";
import { useAuth } from "@/context/auth/AuthContext";
import { CurrentTabProvider } from "@/context/CurrentTabContext";
import {
  FormFieldVisibilityProvider,
  useFormFieldVisibility,
} from "@/context/FormFieldVisibilityContext";
import { FormVisibilityMenuItems } from "@/components/form-items/FormVisibilityMenuItems";
import { toast } from "@/hooks/use-toast";
import { SupportedLocale } from "@/i18n";
import {
  attachmentsToGalleryImages,
  galleryIndexForAttachment,
  getDefaultAttachment,
  getDefaultImage,
  POSITION_MAP_MULTIPLEPRINT_TO_SAMPLE,
  TypeOptionPosition,
} from "@/lib/image";
import type { Collector, Multiplesample } from "@/lib/types";
import { cn, slugify } from "@/lib/utils";
import { Cross1Icon } from "@radix-ui/react-icons";
import * as Tabs from "@radix-ui/react-tabs";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ChevronDownIcon, Eye, EyeOff, ImageIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import { ModifiableList } from "../../form-items/ModifiableList";
import {
  DoubleMultilingualField,
  MultilingualField,
} from "../../form-items/MultilingualField";
import { AbstractFormSelectFetcher } from "../../form-items/select-fetcher-form";
import LinksManagement from "../../LinksManagement";
import { UniqueImageFullScreen } from "../../modal/UniqueImageFullScreen";
import { BadgeCell } from "../../table/CustomCell";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "../../ui/menubar";

type ExemplaireSheetFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exemplaireId: string | undefined;
  multipleprintId: string;
  initialData?: Partial<Multiplesample>;
};

function FieldVisibilityMenuItem() {
  const { t } = useTranslation();
  const { allFieldsMinimized, minimizeAllFields, expandAllFields } =
    useFormFieldVisibility();

  return (
    <MenubarItem
      onClick={() =>
        allFieldsMinimized ? expandAllFields() : minimizeAllFields()
      }
      className="gap-2"
    >
      {allFieldsMinimized ? (
        <Eye className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
      ) : (
        <EyeOff className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
      )}
      {allFieldsMinimized ? t("formField.showAll") : t("formField.hideAll")}
    </MenubarItem>
  );
}

const ExemplaireSheetFormInner = ({
  open,
  onOpenChange,
  exemplaireId,
  multipleprintId: _multipleprintId,
  initialData,
}: ExemplaireSheetFormProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { isTabVisible } = useFormFieldVisibility();
  const queryClient = useQueryClient();
  const { data: exemplaireData, isLoading } =
    useGetMultipleSampleById(exemplaireId);
  const { mutateAsync: updateExemplaire } = useUpdateMultipleSample();
  const { data: multipleprint } = useGetMultiplePrint(_multipleprintId);
  const { user, instanceData } = useAuth();
  const provenanceMode = instanceData?.provenanceMode ?? false;
  const isReader = user?.role === "reader" || false;
  const [isMediaDrawerOpen, setIsMediaDrawerOpen] = useState(false);
  const { data: exemplaireAttachments } =
    useGetAttachmentsByDocId(exemplaireId);

  const defaultExemplaireImage = useMemo(
    () =>
      exemplaireAttachments
        ? getDefaultImage(exemplaireAttachments, "multiplesample")
        : undefined,
    [exemplaireAttachments],
  );

  const exemplaireGalleryImages = useMemo(
    () => attachmentsToGalleryImages(exemplaireAttachments),
    [exemplaireAttachments],
  );

  const exemplaireGalleryInitialIndex = useMemo(() => {
    if (
      !exemplaireAttachments?.length ||
      exemplaireGalleryImages.length === 0
    ) {
      return 0;
    }
    const defaultAttachment = getDefaultAttachment(
      exemplaireAttachments,
      "multiplesample",
    );
    return defaultAttachment
      ? galleryIndexForAttachment(exemplaireGalleryImages, defaultAttachment.id)
      : 0;
  }, [exemplaireAttachments, exemplaireGalleryImages]);

  const handleInvalidateAttachments = () => {
    if (!exemplaireId) return;
    queryClient.invalidateQueries({
      queryKey: ["attachment", { docId: exemplaireId }],
    });
    queryClient.invalidateQueries({ queryKey: ["multiplesamples"] });
    queryClient.invalidateQueries({
      queryKey: ["multiplesample", exemplaireId],
    });
  };

  const computeExemplaireImageName = (data?: ImageFormData) => {
    const parts = [
      exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ??
        exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumero,
      data?.position ? t(data.position) : undefined,
    ].filter(Boolean);
    return parts.length
      ? parts.map((s) => slugify(String(s))).join("-")
      : "exemplaire";
  };

  const copyFromMaster = useMemo(
    () => ({
      masterDocId: _multipleprintId,
      masterPositionType: "multipleprint" as TypeOptionPosition,
      positionMap: POSITION_MAP_MULTIPLEPRINT_TO_SAMPLE,
      fallbackPosition: "exemplairePositionPhotoAutre",
    }),
    [_multipleprintId],
  );

  const defaultValues = useMemo(() => {
    const merged = { ...initialData, ...exemplaireData };
    return {
      ...merged,
      // Le libelle est publie par defaut : une fiche creee avant la colonne
      // ne doit pas se retrouver avec le titre masque.
      exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli:
        merged.exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli ??
        true,
    };
  }, [initialData, exemplaireData]);

  const form = useForm<FieldValues>({
    reValidateMode: "onSubmit",
    defaultValues,
  });

  const hasInitialized = useRef(false);
  const lastExemplaireId = useRef<string | undefined>(exemplaireId);
  const lastOpenState = useRef<boolean>(open);

  useEffect(() => {
    const idChanged = lastExemplaireId.current !== exemplaireId;
    const sheetJustOpened = open && !lastOpenState.current;

    if (idChanged || sheetJustOpened) {
      hasInitialized.current = false;
      lastExemplaireId.current = exemplaireId;
    }
    lastOpenState.current = open;

    const shouldWaitForData = exemplaireId && isLoading;
    const dataIsReady = exemplaireId ? !isLoading : true;

    if (
      !hasInitialized.current &&
      !shouldWaitForData &&
      dataIsReady &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      form.reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [defaultValues, form, exemplaireId, open, exemplaireData, isLoading]);

  const onSubmit = async (
    _data: FieldValues,
    publishState: "draft" | "published" = "draft",
  ) => {
    if (!exemplaireId) return;

    const raw = form.getValues();
    const omitKeys = [
      "id",
      "instanceId",
      "multipleprints",
      "history",
      "multipleSampleInAuction",
      "multipleSampleInExhibitions",
      "multipleSampleInBibliography",
      "updatedBy",
      "createdBy",
      "updatedAt",
      "createdAt",
      "attachments",
    ];
    const submitData: Record<string, unknown> = {};
    Object.keys(raw).forEach((key) => {
      if (!omitKeys.includes(key) && raw[key] !== undefined) {
        submitData[key] = raw[key];
      }
    });

    submitData["exemplaireDansEstampesMultiplesDescriptionStatus"] =
      publishState;

    try {
      await updateExemplaire({
        data: submitData,
        where: { id: exemplaireId },
      });
      toast({
        title: t("recordSuccessfullyUpdated"),
        duration: 3000,
        variant: "success",
      });
    } catch (err) {
      if (
        isAxiosError(err) &&
        (err.response?.status === 409 || err.response?.status === 422)
      ) {
        form.setError(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
          {
            message: t("itemWithSameIdAlreadyExists"),
          },
        );
        return;
      }
      toast({
        title: t("errorUpdatingRecord"),
        description: isAxiosError(err) ? err.message : t("unknownError"),
        variant: "destructive",
      });
    }
  };

  const title = useMemo(() => {
    return multipleprint?.estampesMultiplesDescriptionTitre
      ? multipleprint?.estampesMultiplesDescriptionTitre +
          " - " +
          multipleprint?.estampesMultiplesDescriptionDateDeCreation
      : "";
  }, [multipleprint]);

  const watchedNumeroEnregistrement = form.watch(
    "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
  );
  const watchedNumero = form.watch(
    "exemplaireDansEstampesMultiplesDescriptionNumero",
  );

  /** Ligne 2 du header : « Détails de l'exemplaire n° … (copie) ». */
  const formattedSheetSubtitle = useMemo(() => {
    const numeroEnregistrement =
      watchedNumeroEnregistrement ??
      exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ??
      initialData?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ??
      "";
    const numero =
      watchedNumero ??
      exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumero ??
      initialData?.exemplaireDansEstampesMultiplesDescriptionNumero ??
      "";
    const base =
      `${t("exemplaireKeyLabelTitleForm")} ${numeroEnregistrement}`.trim();
    return numero ? `${base} (${numero})` : base;
  }, [
    t,
    watchedNumeroEnregistrement,
    watchedNumero,
    exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
    exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumero,
    initialData?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
    initialData?.exemplaireDansEstampesMultiplesDescriptionNumero,
  ]);

  const tabs = [
    {
      value: "description",
      label: t("exemplaireTabDescription"),
      nonHideable: true,
    },
    { value: "dimensions", label: t("exemplaireTabDimensions") },
    {
      value: "historique",
      label: provenanceMode
        ? t("exemplaireTabProvenance")
        : t("exemplaireTabHistorique"),
    },
    { value: "links", label: t("exemplaireTabLinkedEntries") },
    { value: "technicalNotes", label: t("exemplaireTabTechnicalNotes") },
    { value: "conditionReport", label: t("exemplaireTabConditionReport") },
    { value: "mouvement", label: t("exemplaireTabMouvementsPrets") },
    {
      value: "admin-docs",
      label: t("artworksValidationsDocumentsAdministratifs"),
    },
  ];

  const exemplaireLinksConfig = useMemo<ConfigurationTypeFile>(
    () => ({
      fields: [
        {
          name: "links",
          type: "links" as const,
          label: t("linkedEntries"),
          links: [
            {
              name: "relatedArtwork",
              label: t("entryartworks"),
              type: "link-simple" as const,
            },
            {
              name: "multipleprintInMultipleSample",
              label: t("exemplaireTabMultiples"),
              type: "link-simple" as const,
            },
            {
              name: "relatedMultiplesample",
              label: t("linkedMultiplesamples"),
              type: "link-simple" as const,
            },
            {
              name: "multipleSampleInExhibitions",
              label: t("entryexhibitions"),
              type: "link-complex" as const,
              fields: [
                {
                  name: "exhibition.expositionsDescriptionNumeroExpo",
                  label: t("exhibitionDescriptionNumeroExpo"),
                  type: "text" as const,
                  className: "w-full",
                  disabled: true,
                },
                {
                  name: "exhibition.expositionsDescriptionResume",
                  label: t("exhibitionDescriptionResume"),
                  type: "richtext" as const,
                  className: "w-full",
                  disabled: true,
                },
                {
                  name: "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
                  label: t(
                    "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
                  ),
                  type: "text" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansExpositionsNotes",
                  label: t("multiplesampleDansExpositionsNotes"),
                  type: "richtext" as const,
                  className: "w-full",
                },
              ],
            },
            {
              name: "multipleSampleInAuction",
              label: t("entryauctions"),
              type: "link-complex" as const,
              fields: [
                {
                  name: "auction.ventesDescriptionNumeroVente",
                  label: t("auctionDescriptionNumeroVente"),
                  type: "text" as const,
                  className: "w-full",
                  disabled: true,
                },
                {
                  name: "auction.ventesDescriptionResume",
                  label: t("auctionDescriptionResume"),
                  type: "richtext" as const,
                  className: "w-full",
                  disabled: true,
                },
                {
                  name: "multiplesampleDansVentesDescriptionNumeroLot",
                  label: t("multiplesampleDansVentesDescriptionNumeroLot"),
                  type: "text" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionReproduitPage",
                  label: t("multiplesampleDansVentesDescriptionReproduitPage"),
                  type: "text" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionEstimationBasse",
                  label: t(
                    "multiplesampleDansVentesDescriptionEstimationBasse",
                  ),
                  type: "inputSelectCurrency" as const,
                  className: "w-full",
                  defaultSelectValue: "EUR",
                },
                {
                  name: "multiplesampleDansVentesDescriptionEstimationHaute",
                  label: t(
                    "multiplesampleDansVentesDescriptionEstimationHaute",
                  ),
                  type: "inputSelectCurrency" as const,
                  className: "w-full",
                  defaultSelectValue: "EUR",
                },
                {
                  name: "multiplesampleDansVentesDescriptionVendu",
                  label: t("multiplesampleDansVentesDescriptionVendu"),
                  type: "checkbox" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionResultatNonListe",
                  label: t(
                    "multiplesampleDansVentesDescriptionResultatNonListe",
                  ),
                  type: "checkbox" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionNonVenu",
                  label: t("multiplesampleDansVentesDescriptionNonVenu"),
                  type: "checkbox" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionNonVendu",
                  label: t("multiplesampleDansVentesDescriptionNonVendu"),
                  type: "checkbox" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionNonReproduit",
                  label: t("multiplesampleDansVentesDescriptionNonReproduit"),
                  type: "checkbox" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionRetire",
                  label: t("multiplesampleDansVentesDescriptionRetire"),
                  type: "checkbox" as const,
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionMiseAPrix",
                  label: t("multiplesampleDansVentesDescriptionMiseAPrix"),
                  type: "inputSelectCurrency" as const,
                  defaultSelectValue: "EUR",
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionPrixMarteau",
                  label: t("multiplesampleDansVentesDescriptionPrixMarteau"),
                  type: "inputSelectCurrency" as const,
                  defaultSelectValue: "EUR",
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionPrixAvecFrais",
                  label: t("multiplesampleDansVentesDescriptionPrixAvecFrais"),
                  type: "inputSelectCurrency" as const,
                  defaultSelectValue: "EUR",
                  className: "w-full",
                },
                {
                  name: "multiplesampleDansVentesDescriptionNotes",
                  label: t("multiplesampleDansVentesDescriptionNotes"),
                  type: "richtext" as const,
                  className: "w-full",
                },
              ],
            },
            {
              name: "multipleSampleInBibliography",
              label: t("entrybibliographies"),
              type: "link-complex" as const,
              fields: [
                {
                  type: "row",
                  className: "items-start w-full",
                  children: [
                    {
                      name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      type: "text" as const,
                      className: "w-full",
                      disabled: true,
                    },
                  ],
                },
                {
                  type: "row",
                  className: "items-start w-full",
                  children: [
                    {
                      name: "bibliography.bibliographieDescriptionResumeLong",
                      label: t("bibliographieDescriptionResumeLong"),
                      type: "richtext" as const,
                      className: "w-full",
                      disabled: true,
                    },
                  ],
                },
                {
                  type: "row",
                  className: "grid grid-cols-2 w-full gap-4",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionReproduitPage",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionReproduitPage",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                    {
                      name: "multiplesampleDansBibliographieDescriptionReproduitPlanche",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionReproduitPlanche",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "grid grid-cols-2 w-full -mt-3 pt-0",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionPlancheNonNumerotee",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionPlancheNonNumerotee",
                      ),
                      type: "checkbox" as const,
                      className: "w-full col-start-2",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "grid grid-cols-2 w-full gap-4",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionCitePage",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionCitePage",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "grid grid-cols-2 w-full gap-4",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionListePage",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionListePage",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                    {
                      name: "multiplesampleDansBibliographieDescriptionSousNumero",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionSousNumero",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "items-start w-full",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionPhotographe",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionPhotographe",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "grid grid-cols-2 w-full gap-4",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionNonPagine",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionNonPagine",
                      ),
                      type: "checkbox" as const,
                      className: "w-full",
                    },
                    {
                      name: "multiSampleInBiblioDescReproCover",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionReproductionCouverture",
                      ),
                      type: "checkbox" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "items-start w-full",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionReproductionCouleur",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionReproductionCouleur",
                      ),
                      type: "checkbox" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "items-start w-full",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieDescriptionTexteLibre",
                      label: t(
                        "multiplesampleDansBibliographieDescriptionTexteLibre",
                      ),
                      type: "text" as const,
                      className: "w-full",
                    },
                  ],
                },
                {
                  type: "row",
                  className: "items-start w-full",
                  children: [
                    {
                      name: "multiplesampleDansBibliographieNotes",
                      label: t("multiplesampleDansBibliographieNotes"),
                      type: "richtext" as const,
                      className: "w-full",
                    },
                  ],
                },
              ],
            },
            {
              name: "relatedAudiovisual",
              label: t("entryaudiovisuals"),
              type: "link-simple" as const,
            },
            {
              name: "relatedDocument",
              label: t("entrydocuments"),
              type: "link-simple" as const,
            },
            {
              name: "relatedWriting",
              label: t("entrywritings"),
              type: "link-simple" as const,
            },
            {
              name: "relatedPhotography",
              label: t("entryphotographies"),
              type: "link-simple" as const,
            },
            {
              name: "externalLinks",
              label: t("entryExternalLinks"),
              type: "link-simple" as const,
            },
          ],
        },
      ],
    }),
    [t],
  );

  const [activeTab, setActiveTab] = useState(tabs[0]?.value ?? "description");

  // Tabs the user chose to hide through the visibility grid (description stays).
  const visibleTabs = useMemo(
    () => tabs.filter((tab) => tab.nonHideable || isTabVisible(tab.value)),
    // `tabs` is rebuilt each render; `isTabVisible` changes with the grid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible, provenanceMode, t],
  );

  // If the active tab gets hidden, fall back to the first visible one.
  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab) && visibleTabs[0]) {
      setActiveTab(visibleTabs[0].value);
    }
  }, [visibleTabs, activeTab]);

  const shouldDisplayVolume =
    form.watch("exemplaireDansEstampesDimensionsIsProfondeur") || false;

  if (isLoading && exemplaireId) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-7xl px-0">
          <div className="flex items-center justify-center h-full">
            <p>{t("loading")}</p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="sm:max-w-7xl w-full px-0 max-h-screen overflow-y-auto overflow-x-hidden flex flex-col"
        closeIconLeft={false}
        showPrimitiveCross={false}
      >
          <SheetHeader className="p-8 pt-4 pb-0 gap-2 flex-shrink-0 flex flex-row items-start justify-between min-w-0">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <div className="flex items-center gap-2 mt-0.5 shrink-0">
                <SheetClose>
                  <Cross1Icon className="h-4 w-4 relative text-gray-400" />
                </SheetClose>
                <BadgeCell
                  value={t(
                    exemplaireData?.exemplaireDansEstampesMultiplesDescriptionStatus ??
                      "draft",
                  )}
                  variant={
                    exemplaireData?.exemplaireDansEstampesMultiplesDescriptionStatus ===
                    "published"
                      ? "published"
                      : "draft"
                  }
                />
              </div>
              {/* Titre long : 2 lignes (œuvre + détails exemplaire), pas de truncate. */}
              <SheetTitle className="min-w-0 text-left font-semibold leading-snug whitespace-normal break-words">
                {title ? (
                  <>
                    <span className="block">{title}</span>
                    <span className="block">{formattedSheetSubtitle}</span>
                  </>
                ) : (
                  <span className="block">{formattedSheetSubtitle}</span>
                )}
              </SheetTitle>
            </div>
            <div className="flex items-start justify-end gap-2 !m-0 shrink-0">
              {!isReader && exemplaireId && (
                <ExemplaireCertificatDrawer exemplaireId={exemplaireId} />
              )}
              {!isReader && (
                <Menubar className="border-none">
                  <MenubarMenu>
                    <MenubarTrigger className="px-3 py-2 rounded-lg justify-center items-center gap-2 flex border border-input bg-white hover:bg-slate-100 whitespace-nowrap min-w-fit">
                      <p className="text-sm font-semibold font-['Raleway']">
                        {t("actions")}
                      </p>
                      <ChevronDownIcon className="w-5 h-5" />
                    </MenubarTrigger>
                    <MenubarContent>
                      {exemplaireId && (
                        <MultiplePrintSingleDrawer
                          exemplaireId={exemplaireId}
                          exemplaireTitle={
                            exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ||
                            exemplaireData?.exemplaireDansEstampesMultiplesDescriptionNumero
                          }
                          menuItemTrigger
                        />
                      )}
                      <FieldVisibilityMenuItem />
                      <FormVisibilityMenuItems
                        tabs={tabs}
                        activeTab={activeTab}
                      />
                    </MenubarContent>
                  </MenubarMenu>
                </Menubar>
              )}
              <Menubar className="border-none">
                <MenubarMenu>
                  <MenubarTrigger
                    className={cn(
                      "px-3 py-2 rounded-lg justify-center items-center gap-2 flex bg-primary text-white",
                      isReader && "opacity-50 cursor-default",
                    )}
                    disabled={isReader}
                  >
                    <p className="text-sm font-semibold font-['Raleway']">
                      {vt("bo.form.save", "Enregistrer")}
                    </p>
                    <ChevronDownIcon className="w-5 h-5 " />
                  </MenubarTrigger>
                  <MenubarContent>
                    <MenubarItem
                      onClick={form.handleSubmit((data) =>
                        onSubmit(data, "draft"),
                      )}
                    >
                      {t("saveAsDraft")}
                    </MenubarItem>
                    <MenubarItem
                      onClick={form.handleSubmit((data) =>
                        onSubmit(data, "published"),
                      )}
                    >
                      {t("saveAndPublish")}
                    </MenubarItem>
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </div>
          </SheetHeader>

          <Form {...form}>
            <Tabs.Root
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex flex-col flex-1 min-h-0"
            >
              <Tabs.TabsList className="flex flex-row gap-1 px-8 pb-2 border-b flex-shrink-0 ">
                <div className="bg-[#F1F5F9] rounded-md p-1">
                  {visibleTabs.map((tab) => (
                    <Tabs.TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="px-3 py-1 rounded-md text-[12px] font-medium transition-colors data-[state=active]:bg-white data-[state=active]:text-gray-700  data-[state=inactive]:text-gray-700 hover:text-foreground"
                    >
                      {tab.label}
                    </Tabs.TabsTrigger>
                  ))}
                </div>
              </Tabs.TabsList>

              <CurrentTabProvider value={activeTab}>
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
                <Tabs.TabsContent
                  value="description"
                  className="px-11 py-5 gap-8"
                >
                  <AF.Row className="items-start">
                    <AF.Col className="w-1/2">
                      {/* Attachments / Media library management */}
                      {exemplaireId && (
                        <div className="mb-6 flex items-end justify-end w-full">
                          <StyledDrawer
                            title={t("titreModalGestionImagesExemplaire")}
                            maxWidth="sm:max-w-5xl"
                            sheetProps={{
                              open: isMediaDrawerOpen,
                              onOpenChange: setIsMediaDrawerOpen,
                            }}
                          >
                            <MediaLibrary
                              id={exemplaireId}
                              type={"multiplesample" as TypeOptionPosition}
                              invalidateOnChange={handleInvalidateAttachments}
                              computeImageName={computeExemplaireImageName}
                              embedAttachmentOnSubmit={true}
                              embeddedInDrawer
                              copyFromMaster={copyFromMaster}
                              setImageMode={(mode) => {
                                if (!mode) {
                                  setIsMediaDrawerOpen(false);
                                }
                              }}
                            />
                          </StyledDrawer>

                          <div className="flex items-end justify-end w-full relative">
                            {defaultExemplaireImage && (
                              <UniqueImageFullScreen
                                media={{
                                  url: defaultExemplaireImage,
                                  largeUrl: defaultExemplaireImage,
                                  alt: "",
                                  className:
                                    "w-full max-h-[400px] object-contain object-right",
                                }}
                                gallery={
                                  exemplaireGalleryImages.length > 0
                                    ? exemplaireGalleryImages
                                    : undefined
                                }
                                initialIndex={exemplaireGalleryInitialIndex}
                              />
                            )}
                            {!defaultExemplaireImage && (
                              <div
                                className="w-full h-[300px] bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-slate-400 hover:bg-slate-100 transition-colors"
                                onClick={() => setIsMediaDrawerOpen(true)}
                              >
                                <ImageIcon className="w-12 h-12 text-slate-400" />
                                <p className="text-sm text-slate-500 font-medium">
                                  {t("thumbnailManageImages_action")}
                                </p>
                              </div>
                            )}
                            <Button
                              variant="white"
                              className="absolute bottom-2 right-2 text-gray-950 text-xs sm:text-sm flex gap-2 bg-blue-50 hover:bg-blue-100 border border-slate-200"
                              onClick={() => setIsMediaDrawerOpen(true)}
                            >
                              {t("formulaireBannierePhotosBouton")}
                              <Badge variant="outlined">
                                {exemplaireAttachments?.length ?? "0"}
                              </Badge>
                            </Button>
                          </div>
                        </div>
                      )}
                      <AF.Text name="exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement" />
                      <AF.Text name="exemplaireDansEstampesMultiplesDescriptionNumero" />
                      <AF.Text
                        name="exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"
                        corner={() => (
                          <AF.Switch
                            name="exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli"
                            label={t(
                              "exemplaireDansEstampesMultiplesDescriptionNumeroInventairePublished",
                            )}
                            labelFirst
                          />
                        )}
                      />
                      <AF.Text
                        name="exemplaireDansEstampesMultiplesDescriptionAncienNumero"
                        corner={() => (
                          <AF.Switch
                            name="exemplaireDansEstampesMultiplesDescriptionAncienPublished"
                            labelFirst
                          />
                        )}
                      />
                      <AF.Text
                        name="exemplaireDansEstampesMultiplesDescriptionAutreNumero"
                        corner={() => (
                          <AF.Switch
                            name="exemplaireDansEstampesMultiplesDescriptionAutrePublished"
                            labelFirst
                          />
                        )}
                      />
                      <ModifiableList
                        name="exemplaireDansEstampesMultiplesStatus"
                        listKey="liste-status"
                      />
                      <ModifiableList
                        name="exemplaireDansEstampesMultiplesDescriptionType"
                        listKey="liste-types-multiplesample"
                      />
                      <ModifiableList
                        name="exemplaireDansEstampesMultiplesDescriptionPrinter"
                        listKey="liste-multiplesample-imprimeur"
                      />
                      <ModifiableList
                        name="exemplaireDansEstampesMultiplesDescriptionEditor"
                        listKey="liste-multipleprint-editeur"
                      />
                      <DateQualifierFields
                        names={{
                          date: "exemplaireDansEstampesMultiplesDescriptionDateTirage",
                          unknown:
                            "exemplaireDansEstampesMultiplesDescriptionDateTirageInconnue",
                          uncertain:
                            "exemplaireDansEstampesMultiplesDescriptionDateTirageIncertaine",
                          estimated:
                            "exemplaireDansEstampesMultiplesDescriptionDateTirageEstimee",
                        }}
                      />
                      <MultilingualField
                        render={(lang: SupportedLocale) => (
                          <AF.Col>
                            <ModifiableList
                              listKey="liste-techniques"
                              name={`exemplaireDansEstampesMultiplesTechnique.${lang}`}
                              label={t(
                                "exemplaireDansEstampesMultiplesTechnique",
                              )}
                              lang={lang}
                            />
                            <ModifiableList
                              listKey="liste-supports"
                              name={`exemplaireDansEstampesMultiplesSupport.${lang}`}
                              label={t(
                                "exemplaireDansEstampesMultiplesSupport",
                              )}
                              lang={lang}
                            />
                            <AF.Text
                              name={`exemplaireDansEstampesMultiplesEditionNumber.${lang}`}
                              label={t(
                                "exemplaireDansEstampesMultiplesEditionNumber",
                              )}
                            />
                            <AF.Text
                              name={`exemplaireDansEstampesMultiplesSignatureInscription.${lang}`}
                              label={t(
                                "exemplaireDansEstampesMultiplesSignatureInscription",
                              )}
                            />
                            <ModifiableList
                              listKey="liste-inscriptions-dos"
                              name={`exemplaireDansEstampesMultiplesInscriptionDos.${lang}`}
                              label={t(
                                "exemplaireDansEstampesMultiplesInscriptionDos",
                              )}
                              lang={lang}
                              corner={() => (
                                <AF.Switch
                                  name={`exemplaireDansEstampesMultiplesInscriptionDosPublished.${lang}`}
                                  label={t(
                                    "exemplaireDansEstampesMultiplesInscriptionDosPublished",
                                  )}
                                />
                              )}
                            />
                          </AF.Col>
                        )}
                      />
                    </AF.Col>
                    <AF.Col className="w-1/2">
                      <SeparatorWithText
                        text={t("exemplaireEstimationSeparatorTitle")}
                        className="my-4"
                      />
                      <AF.Text
                        name="exemplaireDansEstampesEstimationDescription"
                        label={t("exemplaireDansEstampesEstimationDescription")}
                      />
                      <AF.SelectCurrency
                        name="exemplaireDansEstampesEstimationAmount"
                        label={t("exemplaireDansEstampesEstimationAmount")}
                        className="w-full"
                      />
                      <SeparatorWithText
                        text={t("exemplaireSaleSeparatorTitle")}
                        className="my-4"
                      />
                      <AF.Text
                        name="exemplaireDansEstampesSaleDescription"
                        label={t("exemplaireDansEstampesSaleDescription")}
                      />
                      <AF.SelectCurrency
                        name="exemplaireDansEstampesSaleAmount"
                        label={t("exemplaireDansEstampesSaleAmount")}
                        className="w-full"
                      />
                      <SeparatorWithText
                        text={t("exemplaireAcquisitionSeparatorTitle")}
                        className="my-4"
                      />
                      <AF.Text
                        name="exemplaireDansEstampesAcquisitionDescription"
                        label={t(
                          "exemplaireDansEstampesAcquisitionDescription",
                        )}
                      />
                      <AF.SelectCurrency
                        name="exemplaireDansEstampesAcquisitionAmount"
                        label={t("exemplaireDansEstampesAcquisitionAmount")}
                        className="w-full"
                      />
                      <AF.RichText
                        name="exemplaireDansEstampesMultiplesDescriptionNotes"
                        label={t(
                          "exemplaireDansEstampesMultiplesDescriptionNotes",
                        )}
                      />
                      <SeparatorWithText
                        text={t("exemplaireStorageSeparatorTitle")}
                        className="my-4"
                      />
                      <ModifiableList
                        listKey="liste-storage-locations-multiplesample"
                        name="exemplaireDansEstampesMultiplesDescriptionLocalisation"
                        label={t(
                          "exemplaireDansEstampesMultiplesDescriptionLocalisation",
                        )}
                      />
                      <ModifiableList
                        listKey="liste-storage-locations-multiplesample"
                        name="exemplaireDansEstampesStorageLocation2"
                        label={t("exemplaireDansEstampesStorageLocation2")}
                      />
                      <ModifiableList
                        listKey="liste-storage-locations-multiplesample"
                        name="exemplaireDansEstampesStorageLocation3"
                        label={t("exemplaireDansEstampesStorageLocation3")}
                      />
                    </AF.Col>
                  </AF.Row>
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="dimensions"
                  className="px-11 py-5 gap-8"
                >
                  <AF.Col>
                    <FourDimensionalInput
                      parentName="exemplaireDansEstampesDimensions"
                      needVolume={shouldDisplayVolume}
                      needSurface={!shouldDisplayVolume}
                    />

                    <AF.RadioGroup
                      groupName="exemplaireDimensionsOeuvreOptionsGroup"
                      label={t("oeuvresDescriptionDimensionsOptionsGroup")}
                      items={[
                        {
                          value: "exemplaireDansEstampesDimensionsVerifiees",
                        },
                        {
                          value: "exemplaireDansEstampesDimensionsInconnues",
                        },
                        {
                          value: "exemplaireDansEstampesDimensionsDouteuses",
                        },
                      ]}
                    />
                    <Separator className="my-4" />
                    <AF.Col className="w-1/2">
                      <TwoDimensionalInput parentName="exemplaireDansEstampesDimensionsImage" />
                    </AF.Col>
                    <AF.RadioGroup
                      groupName="exemplaireDimensionsImageOptionsGroup"
                      label={t("exemplaireDimensionsImageOptionsGroup")}
                      items={[
                        {
                          value:
                            "exemplaireDansEstampesDimensionsImageVerifiees",
                        },
                        {
                          value:
                            "exemplaireDansEstampesDimensionsImageInconnues",
                        },
                        {
                          value:
                            "exemplaireDansEstampesDimensionsImage Douteuses",
                        },
                      ]}
                    />

                    <Separator className="my-4" />
                    <AF.Checkbox name="exemplaireDansEstampesDimensionsIsProfondeur" />

                    <AF.Text
                      className="w-20"
                      name="exemplaireDansEstampesDimensionsPoids"
                      unit="kg"
                    />
                    <AF.Text
                      name="exemplaireDansEstampesDimensionsRaw"
                      label={t("exemplaireDansEstampesDimensionsRaw")}
                      sublabel={t(
                        "exemplaireDansEstampesDimensionsRawSublabel",
                      )}
                    />
                  </AF.Col>
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="historique"
                  className="px-11 py-5 gap-8"
                >
                  <AF.Col>
                    <AbstractFormSelectFetcher
                      label={t("collector")}
                      name="collectorId"
                      fetcher={async () => await getCollectors({})}
                      queryKey={["collectors"]}
                      allowClear={true}
                      getContent={(collector: Collector) => (
                        <>
                          {collector.collectionneursDescriptionNom}{" "}
                          {collector.collectionneursDescriptionPrenom}{" "}
                          {collector.collectionneursDescriptionVille
                            ? `(${collector.collectionneursDescriptionVille})`
                            : ""}
                        </>
                      )}
                      getLabel={(collector: Collector) =>
                        `${collector.collectionneursDescriptionNom} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionVille ? `(${collector.collectionneursDescriptionVille})` : ""}`
                      }
                      getValue="id"
                    />
                    <AF.Checkbox name="exemplaireDansEstampesMultiplesOeuvreNonLocalisee" />
                    <ExemplaireHistoryTable
                      exemplaireId={exemplaireId}
                      disabled={isReader}
                      provenanceMode={provenanceMode}
                    />
                  </AF.Col>
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="links"
                  className="px-11 py-5 gap-8 w-full"
                >
                  {exemplaireId && (
                    <LinksManagement
                      type="multiplesample"
                      id={exemplaireId}
                      data={(exemplaireData ?? initialData) as Multiplesample}
                      configuration={exemplaireLinksConfig}
                      className="pr-0"
                      hideExemplaireColumnsInExhibitionLinks
                    />
                  )}
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="technicalNotes"
                  className="px-11 py-5 gap-8"
                >
                  <AF.RichText
                    name="exemplaireDansEstampesMultiplesDescriptionNotesTechniques"
                    label={t(
                      "exemplaireDansEstampesMultiplesDescriptionNotesTechniques",
                    )}
                  />
                  <div className="flex justify-start w-full my-3">
                    <AF.Switch
                      name="exemplaireDansEstampesMultiplesRemarquesPubliquesPublished"
                      label={t(
                        "exemplaireDansEstampesMultiplesRemarquesPubliquesPublished",
                      )}
                    />
                  </div>
                  <div className="flex justify-start w-full my-3">
                    <AF.Switch
                      name="exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli"
                      label={vt(
                        "exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli",
                        "Publier le libellé (À propos de cette œuvre)",
                      )}
                    />
                  </div>
                  <DoubleMultilingualField
                    name="exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual"
                    render={(lang) => (
                      <AF.RichText
                        name={`exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual.${lang}`}
                        label={t(
                          "artwork.form.oeuvresValidationRemarquesPubliques",
                        )}
                      />
                    )}
                  />
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="conditionReport"
                  className="px-11 py-5 gap-8"
                >
                  <AF.RichText
                    name="exemplaireDansEstampesMultiplesDescriptionNotesConditions"
                    label={t(
                      "exemplaireDansEstampesMultiplesDescriptionNotesConditions",
                    )}
                  />
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="mouvement"
                  className="px-11 py-5 gap-8"
                >
                  <ExemplaireMouvementTable
                    exemplaireId={exemplaireId}
                    disabled={isReader}
                  />
                </Tabs.TabsContent>

                <Tabs.TabsContent
                  value="admin-docs"
                  className="px-11 py-5 gap-8"
                >
                  {exemplaireId ? (
                    <AdministrativeDocumentList
                      multiplesampleId={exemplaireId}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t("administrativeDocumentsAucunResultats")}
                    </p>
                  )}
                </Tabs.TabsContent>
              </div>
              </CurrentTabProvider>
            </Tabs.Root>
          </Form>
      </SheetContent>
    </Sheet>
  );
};

const ExemplaireSheetForm = (props: ExemplaireSheetFormProps) => (
  <FormFieldVisibilityProvider formKey="exemplaire-sheet">
    <ExemplaireSheetFormInner {...props} />
  </FormFieldVisibilityProvider>
);

export default ExemplaireSheetForm;
